import { useState } from "react";
import { Box, Typography, Paper, Button, styled, CircularProgress, Alert } from "@mui/material";
import { useCreateCheckoutSessionMutation } from "Services/subscription";

const PageContainer = styled(Box)({
  maxWidth: 900,
  margin: "0 auto",
  padding: "48px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 32,
  alignItems: "center",
});

const PlansRow = styled(Box)({
  display: "flex",
  gap: 24,
  flexWrap: "wrap",
  justifyContent: "center",
  width: "100%",
});

const PlanCard = styled(Paper)({
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  width: 320,
  display: "flex",
  flexDirection: "column",
  gap: 16,
});

const PrimaryButton = styled(Button)({
  background: "linear-gradient(90deg, #2A7BFF 0%, #3F8CFF 100%)",
  color: "white",
  padding: "10px 24px",
  borderRadius: 8,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  "&:hover": {
    background: "linear-gradient(90deg, #1D63E8 0%, #2A7BFF 100%)",
  },
  "&:disabled": {
    background: "#ccc",
    color: "#fff",
  },
});

type PlanId = "pro" | "growth";

const PLANS: Array<{ id: PlanId; name: string; price: string; description: string }> = [
  { id: "pro", name: "Pro", price: "$99/mo", description: "Full platform access for growing teams." },
  { id: "growth", name: "Growth", price: "$299/mo", description: "Full platform access with additional capacity for scaling teams." },
];

export default function Subscribe() {
  const [createCheckoutSession] = useCreateCheckoutSessionMutation();
  const [loadingPlan, setLoadingPlan] = useState<PlanId | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (plan: PlanId) => {
    setError(null);
    setLoadingPlan(plan);
    try {
      const { url } = await createCheckoutSession({ plan }).unwrap();
      window.location.href = url;
    } catch (err: any) {
      setLoadingPlan(null);
      setError(err?.data?.message || err?.message || "Failed to start checkout. Please try again.");
    }
  };

  return (
    <PageContainer>
      <Box textAlign="center">
        <Typography variant="h2" fontWeight={700} mb={1}>
          Choose your plan
        </Typography>
        <Typography fontSize={16} color="text.secondary">
          Billed monthly. Cancel anytime from Settings.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ width: "100%", maxWidth: 660 }}>
          {error}
        </Alert>
      )}

      <PlansRow>
        {PLANS.map((plan) => (
          <PlanCard key={plan.id}>
            <Typography fontWeight={700} fontSize={20}>
              {plan.name}
            </Typography>
            <Typography fontWeight={700} fontSize={28}>
              {plan.price}
            </Typography>
            <Typography fontSize={14} color="text.secondary" sx={{ flexGrow: 1 }}>
              {plan.description}
            </Typography>
            <PrimaryButton
              fullWidth
              onClick={() => handleSubscribe(plan.id)}
              disabled={loadingPlan !== null}
              startIcon={loadingPlan === plan.id ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {loadingPlan === plan.id ? "Redirecting..." : `Subscribe to ${plan.name}`}
            </PrimaryButton>
          </PlanCard>
        ))}
      </PlansRow>
    </PageContainer>
  );
}